'use client';

import { Box } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { SettingsEditDialog } from '../settings/SettingsEditDialog';
import { hasSameBusinessUnitIds } from '../application-general/application-general.model';
import { useDraftOnOpen } from '../application-general/useDraftOnOpen';
import type { BusinessUnitOption } from './business-unit-selector.model';
import { BusinessUnitSelector } from './BusinessUnitSelector';

export type BusinessUnitsEditDialogProps = {
  open: boolean;
  businessUnits: readonly BusinessUnitOption[];
  allBusinessUnits: readonly BusinessUnitOption[];
  loading?: boolean;
  disabled?: boolean;
  isCreatingBusinessUnit?: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateBusinessUnit: (name: string) => Promise<BusinessUnitOption>;
  onSubmit: (businessUnits: BusinessUnitOption[]) => Promise<boolean>;
};

export function BusinessUnitsEditDialog({
  open,
  businessUnits,
  allBusinessUnits,
  loading,
  disabled,
  isCreatingBusinessUnit,
  onOpenChange,
  onCreateBusinessUnit,
  onSubmit,
}: BusinessUnitsEditDialogProps) {
  const t = useTranslations('designSystem.applicationGeneral');
  const tCommon = useTranslations('designSystem.common');
  const [draft, setDraft] = useDraftOnOpen<readonly BusinessUnitOption[]>({
    open,
    value: businessUnits,
  });

  return (
    <SettingsEditDialog
      open={open}
      title={t('editBusinessUnit')}
      onOpenChange={onOpenChange}
      noInitialFocus
      loading={loading}
      onSubmit={async () => {
        if (await onSubmit([...draft])) {
          onOpenChange(false);
        }
      }}
      isSubmitDisabled={
        disabled ||
        hasSameBusinessUnitIds({ current: businessUnits, next: draft })
      }
      submitText={tCommon('saveChanges')}
    >
      <Box w="full">
        <BusinessUnitSelector
          value={draft}
          allBusinessUnits={allBusinessUnits}
          onValueChange={setDraft}
          onCreateBusinessUnit={onCreateBusinessUnit}
          isCreatingBusinessUnit={isCreatingBusinessUnit}
          placeholder={t('selectBusinessUnits')}
          disabled={disabled}
        />
      </Box>
    </SettingsEditDialog>
  );
}
